import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Detect browser
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('Trident')) return 'IE';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Edg')) return 'Edge Chromium';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
};

// Parse UTM parameters
const getUTMParams = (search: string) => {
  const params = new URLSearchParams(search);
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_content: params.get('utm_content') || null,
    utm_term: params.get('utm_term') || null,
  };
};

const PageAnalytics = () => {
  const location = useLocation();
  const lastTrackedPath = useRef<string | null>(null);
  const debounceTimer = useRef<number | null>(null);

  useEffect(() => {
    // Debounce to prevent duplicate tracking on quick navigation
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = window.setTimeout(() => {
      // Don't track the same path twice in quick succession
      if (lastTrackedPath.current === location.pathname + location.search) {
        return;
      }

      // Don't track admin pages
      if (location.pathname.startsWith('/admin')) {
        return;
      }

      lastTrackedPath.current = location.pathname + location.search;

      const sessionId = getSessionId();
      const utmParams = getUTMParams(location.search);

      // Track page view asynchronously
      const trackPageView = async () => {
        try {
          await supabase.from('page_analytics').insert({
            page_path: location.pathname,
            page_title: document.title,
            event_type: 'page_view',
            session_id: sessionId,
            referrer: document.referrer || null,
            device_type: getDeviceType(),
            browser: getBrowser(),
            screen_width: window.innerWidth,
            ...utmParams,
          });
        } catch (error) {
          // Silently fail - analytics shouldn't break the app
          console.debug('Analytics tracking failed:', error);
        }
      };

      // Use requestIdleCallback if available for non-blocking tracking
      if ('requestIdleCallback' in window) {
        (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(trackPageView);
      } else {
        setTimeout(trackPageView, 0);
      }
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [location.pathname, location.search]);

  return null;
};

// Export custom event tracking function
export const trackCustomEvent = async (
  eventType: string,
  eventData?: Record<string, unknown>
) => {
  try {
    const sessionId = getSessionId();
    await supabase.from('page_analytics').insert({
      page_path: window.location.pathname,
      page_title: document.title,
      event_type: eventType,
      session_id: sessionId,
      device_type: getDeviceType(),
      browser: getBrowser(),
      screen_width: window.innerWidth,
      referrer: document.referrer || null,
    });
  } catch (error) {
    console.debug('Event tracking failed:', error);
  }
};

export default PageAnalytics;
